import React, { Fragment, useEffect, useRef, useState } from "react";
import { AudioRecorder } from "react-audio-voice-recorder";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { currentAppLanguage } from "../../utils/appLanguages";
import { localizedExerciseName, localizedField } from "../../utils/localizedCatalogFields";
import SendIcon from "@mui/icons-material/Send";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import SportsTennisIcon from "@mui/icons-material/SportsTennis";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import GroupIcon from "@mui/icons-material/Group";

import WaveSurferComponent from "../../components/WaveSurferComponent";
import {
  audioupload,
  videoupload,
  exdetails,
} from "../../components/funtionsBD/Mainservice";
import "./ExerciseFeedback.css";
import { initializeSocket } from "../../socket";
import { AddHeader } from "../../components/AddHeader";

const PERFORMANCE = [
  { emoji: "\u{1F626}", title: "Very Unsatisfied", num: "1-2" },
  { emoji: "\u{1F641}", title: "Unsatisfied", num: "3-4" },
  { emoji: "\u{1F610}", title: "Neutral", num: "5-6" },
  { emoji: "\u{1F642}", title: "Satisfied", num: "7-8" },
  { emoji: "\u{1F600}", title: "Very Satisfied", num: "9-10" },
];

const DIFFICULTY = [
  { emoji: "\u{1F600}", title: "Very Light", num: "1-2" },
  { emoji: "\u{1F642}", title: "Light", num: "3-4" },
  { emoji: "\u{1F610}", title: "Moderate", num: "5-6" },
  { emoji: "\u{1F641}", title: "Vigorous", num: "7-8" },
  { emoji: "\u{1F626}", title: "Maximum", num: "9-10" },
];

function findRating(list, num) {
  return list.find((i) => i.num === num);
}

export function ExerciseFeedback() {
  const [exerciseInfo, setExerciseInfo] = useState();
  const [players, setplayers] = useState();
  const [htmlFeedBack, setHtmlFeedBack] = useState("");
  const [feedBacks, setFeedBacks] = useState([]);
  const [msg, setmsg] = useState("all");
  const [autolist, setautolist] = useState("");
  const [chatOpen, setChatOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const chatEndRef = useRef(null);

  const location = useLocation();
  const currentlng = currentAppLanguage(i18n);
  const token = localStorage.getItem("token");
  const userid = JSON.parse(localStorage.getItem("user")).user_id;
  const socket = initializeSocket(token);

  useEffect(() => {
    getdetails();
  }, []);

  useEffect(() => {
    const te_id = exerciseInfo?.te_id;
    if (!te_id) return;

    const onConnectedUsers = (users) => {
      socket.off("connectedUsers", onConnectedUsers);
      const exercisePlayers = (users || [])
        .filter((u) => u.role === "player" && u.user_id)
        .map((u) => ({
          user_id: u.user_id,
          user_first_name: u.user_first_name,
          user_alias: u.user_alias,
          user_image: u.user_image,
          role: "player",
        }));
      setplayers(exercisePlayers);
    };

    socket.on("connectedUsers", onConnectedUsers);
    socket.emit("getConnectedUsers", { te_id });

    return () => {
      socket.off("connectedUsers", onConnectedUsers);
    };
  }, [exerciseInfo]);

  useEffect(() => {
    const te_id = exerciseInfo?.te_id;
    if (!te_id) return;

    const ts_id =
      location.state?.selectedSession?.ts_id || exerciseInfo?.ts_id;

    const onPreviousMessages = (message) => setFeedBacks(message);
    socket.on("previousMessages", onPreviousMessages);

    if (msg === "all") {
      socket.emit("getPreviousMessages", {
        te_id,
        ts_id,
        group_channel: true,
      });
    } else {
      socket.emit("getPreviousMessages", {
        te_id,
        ts_id,
        sender_id: userid,
        receiver_id: msg,
      });
    }

    return () => {
      socket.off("previousMessages", onPreviousMessages);
    };
  }, [msg, exerciseInfo, location.state?.selectedSession?.ts_id]);

  useEffect(() => {
    if (!socket) return;
    socket.on("newMessage", (message) => {
      setFeedBacks((prev) => [...prev, message]);
    });
    return () => {
      socket.off("newMessage");
    };
  }, [socket]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [feedBacks]);

  const getdetails = async () => {
    try {
      const params = { te_id: location.state.id };
      const response = await exdetails(params);
      setExerciseInfo(response.data.data);
    } catch (error) {
      console.log(error);
    }
  };

  const addAudioElement = async (blob) => {
    const updatedBlob = new Blob([blob], { type: "audio/mpeg" });
    try {
      const params = new FormData();
      params.append("voiceMessage", updatedBlob);
      const response = await audioupload(params);
      const messageData = {
        te_id: exerciseInfo.te_id,
        sender_id: userid,
        message_type: "voice",
        text_message: null,
        voice_message_url: response.data.data,
        ...(msg !== "all" && { receiver_id: msg }),
      };
      socket.emit("sendMessage", messageData, (ack) => {
        if (ack.error) console.error("Message not sent:", ack.error);
      });
    } catch (error) {
      console.log(error);
    }
  };

  const addVideoElement = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    try {
      const params = new FormData();
      params.append("videoMessage", file);
      const response = await videoupload(params);
      const messageData = {
        te_id: exerciseInfo.te_id,
        sender_id: userid,
        message_type: "video",
        text_message: null,
        voice_message_url: null,
        video_message_url: response.data.data,
        ...(msg !== "all" && { receiver_id: msg }),
      };
      socket.emit("sendMessage", messageData, (ack) => {
        if (ack.error) console.error("Message not sent:", ack.error);
      });
      event.target.value = "";
    } catch (error) {
      console.log(error);
    }
  };

  const handleSend = () => {
    if (!htmlFeedBack?.trim()) return;
    const messageData = {
      te_id: exerciseInfo.te_id,
      sender_id: userid,
      message_type: "text",
      text_message: htmlFeedBack,
      voice_message_url: null,
      ...(msg !== "all" && { receiver_id: msg }),
    };
    socket.emit("sendMessage", messageData, (ack) => {
      if (ack.error) console.error("Message not sent:", ack.error);
    });
    setHtmlFeedBack("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handlePlayerSelect = (player) => {
    const id = player.user_id;
    setmsg(id);
    setChatOpen(true);
    if (id === "all") {
      setautolist("");
    } else {
      const match = exerciseInfo?.player_details?.find(
        (info) => info.user_id === id
      );
      setautolist(match ? match.auto_evaluation_data : "");
    }
  };

  const handleBackToList = () => {
    setChatOpen(false);
  };

  const selectedPlayer =
    msg === "all"
      ? null
      : players?.find((p) => p.user_id === msg);

  const selectedName =
    msg === "all"
      ? t("all")
      : selectedPlayer?.user_alias || selectedPlayer?.user_first_name || "";

  const perfRating = autolist
    ? findRating(PERFORMANCE, autolist.exercise_perf)
    : null;
  const diffRating = autolist
    ? findRating(DIFFICULTY, autolist.perceived_exertion)
    : null;

  return (
    <>
      <AddHeader title={t("feedback")} />

      <div className="fb-page">
        {/* ── Compact Exercise Info ── */}
        <div className="fb-exercise-bar">
          <div className="fb-exercise-bar__icon">
            <SportsTennisIcon fontSize="inherit" />
          </div>
          <div className="fb-exercise-bar__info">
            <h3 className="fb-exercise-bar__name">
              {localizedExerciseName(exerciseInfo, currentlng)}
            </h3>
            <div className="fb-exercise-bar__meta">
              {exerciseInfo?.skills?.map((skill, index) => (
                <span key={index} className="fb-exercise-bar__tag">
                  {localizedField(skill, "skill_name", currentlng)}
                </span>
              ))}
              {exerciseInfo?.ex_objective && (
                <span className="fb-exercise-bar__objective">
                  {exerciseInfo.ex_objective}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Two-Panel Body ── */}
        <div className={`fb-body ${chatOpen ? "fb-body--chat-open" : ""}`}>

          {/* ── Left: Player Sidebar ── */}
          <div className="fb-sidebar">
            <div className="fb-sidebar__header">
              {t("conversations") || "Conversations"}
            </div>
            <div className="fb-sidebar__list">
              {/* Group chat row */}
              <div
                className={`fb-player-row ${msg === "all" && chatOpen ? "fb-player-row--active" : ""}`}
                onClick={() => handlePlayerSelect({ user_id: "all" })}
              >
                <div className="fb-player-row__all-icon">
                  <GroupIcon fontSize="inherit" />
                </div>
                <div className="fb-player-row__info">
                  <p className="fb-player-row__name">{t("all")}</p>
                  <p className="fb-player-row__sub">{t("groupMessage") || "Group message"}</p>
                </div>
              </div>

              {/* Individual player rows */}
              {players?.map((player, index) => (
                <div
                  key={index}
                  className={`fb-player-row ${msg === player.user_id && chatOpen ? "fb-player-row--active" : ""}`}
                  onClick={() => handlePlayerSelect(player)}
                >
                  <div className="fb-player-row__avatar">
                    {player.user_image ? (
                      <img src={player.user_image} alt="" />
                    ) : (
                      (player.user_alias || player.user_first_name || "?")
                        .charAt(0)
                        .toUpperCase()
                    )}
                  </div>
                  <div className="fb-player-row__info">
                    <p className="fb-player-row__name">
                      {player.user_alias || player.user_first_name}
                    </p>
                    <p className="fb-player-row__sub">
                      {t("privateMessage") || "Private message"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Conversation Panel ── */}
          <div className="fb-conversation">
            {/* Conversation header */}
            <div className="fb-conv-header">
              <button
                type="button"
                className="fb-conv-header__back"
                onClick={handleBackToList}
              >
                <ArrowBackIosNewIcon fontSize="inherit" />
              </button>
              {msg === "all" ? (
                <div className="fb-conv-header__avatar fb-conv-header__avatar--all">
                  <GroupIcon fontSize="inherit" />
                </div>
              ) : (
                <div className="fb-conv-header__avatar">
                  {selectedPlayer?.user_image ? (
                    <img src={selectedPlayer.user_image} alt="" />
                  ) : (
                    (selectedName || "?").charAt(0).toUpperCase()
                  )}
                </div>
              )}
              <div>
                <p className="fb-conv-header__name">{selectedName}</p>
                <p className="fb-conv-header__label">
                  {msg === "all"
                    ? `${players?.length || 0} ${t("players") || "players"}`
                    : t("privateMessage") || "Private message"}
                </p>
              </div>
            </div>

            {/* Auto-evaluation strip */}
            {autolist && (perfRating || diffRating) && (
              <div className="fb-eval-strip">
                {perfRating && (
                  <div className="fb-eval-card">
                    <span className="fb-eval-card__emoji">{perfRating.emoji}</span>
                    <div className="fb-eval-card__content">
                      <p className="fb-eval-card__label">{t("ex_perfomance")}</p>
                      <p className="fb-eval-card__value">{perfRating.title}</p>
                    </div>
                  </div>
                )}
                {diffRating && (
                  <div className="fb-eval-card">
                    <span className="fb-eval-card__emoji">{diffRating.emoji}</span>
                    <div className="fb-eval-card__content">
                      <p className="fb-eval-card__label">{t("ex_intensity")}</p>
                      <p className="fb-eval-card__value">{diffRating.title}</p>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Chat messages */}
            <div className="fb-chat">
              {!feedBacks?.length ? (
                <div className="fb-chat__empty">
                  <span className="fb-chat__empty-icon">💬</span>
                  <span className="fb-chat__empty-text">
                    {t("msg")}...
                  </span>
                </div>
              ) : (
                feedBacks.map((fb, idx) => {
                  const isMine = fb.sender?.user_id === userid;
                  const showGroup =
                    idx === 0 ||
                    feedBacks[idx - 1]?.te_id !== fb.te_id;
                  const isCurrentEx = fb.te_id === exerciseInfo?.te_id;
                  const ctx = fb.exercise_context || {};
                  const groupTitle = ctx.label || t("exercise") || "Exercise";

                  return (
                    <Fragment key={fb.message_id}>
                      {showGroup && (
                        <div
                          className={`fb-chat__group-label ${isCurrentEx ? "fb-chat__group-label--current" : ""}`}
                        >
                          <span className="fb-chat__group-label__text">
                            {groupTitle}
                            {ctx.te_order != null && (
                              <span className="fb-chat__group-label__order">
                                {" "}
                                · #{ctx.te_order}
                              </span>
                            )}
                          </span>
                          {isCurrentEx && (
                            <span className="fb-chat__group-label__badge">
                              {t("currentExercise") || "This exercise"}
                            </span>
                          )}
                        </div>
                      )}
                      <div
                        className={`fb-msg ${isMine ? "fb-msg--mine" : "fb-msg--theirs"} ${isCurrentEx ? "fb-msg--current-ex" : ""}`}
                      >
                        <div className="fb-msg__avatar">
                          {fb.sender?.user_image ? (
                            <img src={fb.sender.user_image} alt="" />
                          ) : (
                            (fb.sender?.user_alias || fb.sender?.user_first_name || "?")
                              .charAt(0)
                              .toUpperCase()
                          )}
                        </div>
                        <div className="fb-msg__bubble">
                          {fb.message_type === "text" && fb.text_message}
                          {fb.message_type === "voice" && (
                            <div className="fb-msg__audio">
                              <WaveSurferComponent audioUrl={fb.voice_message_url} />
                            </div>
                          )}
                          {fb.message_type === "video" && (
                            <div className="fb-msg__video">
                              <video controls>
                                <source src={fb.video_message_url} type="video/mp4" />
                              </video>
                            </div>
                          )}
                          {fb.message_type === "evaluation" && fb.video_message_url && (
                            <div className="fb-msg__video fb-msg__video--evaluation">
                              <p className="fb-msg__eval-label">
                                {t("evaluationVideo") || "Evaluation Video"}
                              </p>
                              <video controls playsInline preload="metadata">
                                <source src={fb.video_message_url} type="video/mp4" />
                              </video>
                              {fb.text_message && (
                                <p className="fb-msg__eval-caption">{fb.text_message}</p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </Fragment>
                  );
                })
              )}
              <div ref={chatEndRef} />
            </div>

            {/* Composer */}
            <div className="fb-composer">
              <input
                type="text"
                className="fb-composer__input"
                placeholder={t("msg")}
                onChange={(e) => setHtmlFeedBack(e.target.value)}
                onKeyDown={handleKeyDown}
                value={htmlFeedBack}
              />
              <div className="fb-composer__actions">
                {htmlFeedBack?.trim() ? (
                  <button
                    className="fb-composer__btn fb-composer__btn--send"
                    onClick={handleSend}
                  >
                    <SendIcon fontSize="inherit" />
                  </button>
                ) : (
                  <>
                    <input
                      type="file"
                      accept="video/*"
                      onChange={addVideoElement}
                      style={{ display: "none" }}
                      id="video-upload-input"
                    />
                    <label
                      htmlFor="video-upload-input"
                      className="fb-composer__video-label"
                    >
                      <VideocamOutlinedIcon fontSize="inherit" />
                    </label>
                    <React.StrictMode>
                      <AudioRecorder
                        onRecordingComplete={addAudioElement}
                        audioTrackConstraints={{
                          noiseSuppression: false,
                          echoCancellation: false,
                        }}
                        downloadOnSavePress={false}
                        showVisualizer={true}
                        downloadFileExtension="webm"
                      />
                    </React.StrictMode>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
